import {
  Card,
  SelectableChip as LargeChip,
} from "@dropbox/dash-component-library";
import { Button } from "@dropbox/dig-components/buttons";
import { Chip } from "@dropbox/dig-components/chip";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  AppsLine,
  AvailableOfflineSync,
  CheckmarkCircleFill,
  CheckmarkCircleLine,
} from "@dropbox/dig-icons/assets";

import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Add_DashConnectedAccount } from "@mirage/analytics/events/types/add_dash_connected_account";
import { useGetSuggestedApps } from "@mirage/growth/onboarding/hooks/useGetSuggestedApps";
import { useOnHideClick } from "@mirage/growth/onboarding/setup-module/shared/useOnHideClick";
import { Link } from "@mirage/link/Link";
import { useConnectorsUI } from "@mirage/mosaics/SettingsPage/useConnectorsUI";
import { useOnboardingValues } from "@mirage/service-onboarding/hooks";
import { useConnectConnector } from "@mirage/settings/hooks/connectors/useConnectConnector";
import type { ConnectorName } from "@mirage/shared/connectors";
import { getConnectorDisplayConfig } from "@mirage/shared/connectors";
import Sentry from "@mirage/shared/sentry";

import { ConnectorIcon } from "@mirage/shared/icons";
import { showSnackbar } from "@mirage/shared/snackbar";
import i18n, { Trans } from "@mirage/translations";
import { useCallback, useState } from "react";
import { ModuleItemContainer } from "./shared/ModuleItemContainer";
import styles from "./styles.module.css";

import { PAP_Open_DashConnectAppPage } from "@mirage/analytics/events/types/open_dash_connect_app_page";
import type { UIConnector } from "@mirage/shared/types";
import { useSetupModuleShown } from "./shared/useSetupModuleShown";

export const adminPrioritizedAppList: Array<ConnectorName> = [
  "onedrive",
  "googledrive",
  "sharepoint",
];

export const memberPrioritizedAppList: Array<ConnectorName> = [
  "outlook",
  "googleCalendar",
  "gmail",
  "notion",
  "custom.smartsheet",
  "asana",
  "github",
  "miro",
  "onenote",
  "custom.lucidchart",
  "dropbox",
];

interface Props {
  appList: ConnectorName[];
}

export const AddAppsModuleItem = ({ appList }: Props) => {
  const { onboardingValues, setOnboardingValue } = useOnboardingValues();
  const { connectorsUrl } = useConnectorsUI();
  const { suggestedApps, hasFetchedSuggestedApps } = useGetSuggestedApps(
    4,
    appList,
  );
  const onHideClick = useOnHideClick("add_apps");
  useSetupModuleShown(
    "add_apps",
    !!onboardingValues?.dismissedSetupModuleAddApps,
  );

  const handleOnHideClick = () => {
    onHideClick(
      <Trans
        i18nKey={"setup_module_hide_apps"}
        components={{
          buttonLink: <Link variant="monochromatic" href={connectorsUrl} />,
        }}
      />,
    );
    setOnboardingValue("dismissedSetupModuleAddApps", true);
  };

  if (onboardingValues?.dismissedSetupModuleAddApps) {
    return null;
  }

  // If user added all apps, show empty state
  if (hasFetchedSuggestedApps && suggestedApps.length === 0) {
    return (
      <Card.Module>
        <Box display="flex" alignItems="center">
          <UIIcon src={AvailableOfflineSync} />
          <Box flexGrow={1}>
            <Card.Header
              withMargin={false}
              title={i18n.t("setup_module_all_apps_added")}
            />
          </Box>
          <Button variant="opacity" onClick={handleOnHideClick}>
            {i18n.t("dismiss")}
          </Button>
        </Box>
      </Card.Module>
    );
  }

  return (
    <ModuleItemContainer
      label={i18n.t("setup_module_add_apps_title")}
      leftAccessory={AppsLine}
      onHideClick={handleOnHideClick}
      data-testid="setup-module-add-apps"
    >
      <Box className={styles.gap8} display="flex">
        {suggestedApps.map((connector) => (
          <ConnectorChip key={connector.id_attrs?.id} connector={connector} />
        ))}
        {suggestedApps.length > 0 && <SeeAllAppsChip />}
      </Box>
    </ModuleItemContainer>
  );
};

const ConnectorChip = ({ connector }: { connector: UIConnector }) => {
  const [connected, setConnected] = useState(false);
  const { connectorsUrl } = useConnectorsUI();
  const connectConnector = useConnectConnector({
    eventProps: { featureLine: "connector" },
  });

  const { reportPapEvent } = useMirageAnalyticsContext();

  const handleConnectClick = useCallback(async () => {
    try {
      const response = await connectConnector(connector);
      if (response.success) {
        setConnected(true);

        const connectorType = connector.id_attrs?.type;
        reportPapEvent(
          PAP_Add_DashConnectedAccount({
            dashActionSurface: "setup_module",
            actionSurfaceComponent: "setup_module",
            featureLine: "onboarding",
            dashConnectorId: connectorType,
          }),
        );

        showSnackbar({
          title: (
            <Box className={styles.gap8} display="flex" alignItems="center">
              <UIIcon src={CheckmarkCircleLine} />
              <div>
                <Trans
                  i18nKey={"setup_module_app_added"}
                  values={{
                    appName: connector.branding?.display_name,
                  }}
                  components={{
                    appsLink: (
                      <Link variant="monochromatic" href={connectorsUrl} />
                    ),
                  }}
                />
              </div>
            </Box>
          ),
          hideCloseButton: true,
        });
      } else {
        showSnackbar({ title: response.message });
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  }, [connectConnector, connector, connectorsUrl, reportPapEvent]);

  if (connected) {
    return (
      <LargeChip variant="success">
        <LargeChip.LargeIconAccessory>
          <UIIcon src={CheckmarkCircleFill} />
        </LargeChip.LargeIconAccessory>
        <Chip.Content>{connector.branding?.display_name}</Chip.Content>
      </LargeChip>
    );
  }

  return (
    <LargeChip onClick={handleConnectClick}>
      <LargeChip.LargeIconAccessory>
        <ConnectorIcon
          size={16}
          connectorName={
            connector.id_attrs?.type ||
            getConnectorDisplayConfig(connector.id_attrs?.type as ConnectorName)
              .connectorName
          }
          iconUrl={connector.branding?.icon_src}
        />
      </LargeChip.LargeIconAccessory>
      <Chip.Content>{connector.branding?.display_name}</Chip.Content>
    </LargeChip>
  );
};

const SeeAllAppsChip = () => {
  const { openConnectors } = useConnectorsUI();
  const { reportPapEvent } = useMirageAnalyticsContext();

  const handleOnClick = () => {
    reportPapEvent(
      PAP_Open_DashConnectAppPage({
        actionSurfaceComponent: "setup_module",
        featureLine: "onboarding",
      }),
    );
    openConnectors();
  };

  return (
    <Chip className={styles.seeAllAppsChip} onClick={handleOnClick}>
      <Chip.Content>{i18n.t("see_all")}</Chip.Content>
    </Chip>
  );
};
